"use client";

import React from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { HomeIcon, ArrowLeftIcon, SearchIcon, Sparkles, Zap, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Image from 'next/image';

export default function NotFound() {
  const t = useTranslations('NotFound');
  const router = useRouter();

  const handleGoHome = () => {
    router.push(`/${currentLocale}`);
  };

  const handleGoBack = () => {
    router.back();
  };

  const pathname = usePathname();
  const currentLocale = pathname?.match(/^\/(en|ar|fr)/)?.[1] || 'en';

  const handleQuickLink = (path: string) => {
    router.push(`/${currentLocale}${path}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#05092B] via-[#0a0a0a] to-[#10142B] flex items-center justify-center px-4 relative overflow-hidden">
      
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Enhanced floating elements with more variety */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-brand-gradient rounded-full opacity-20 animate-pulse blur-sm"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-r from-[#8b5cf6] to-[#a855f7] rounded-full opacity-30 animate-bounce"></div>
        <div className="absolute bottom-32 left-1/4 w-12 h-12 bg-gradient-to-r from-brand-secondary to-[#8b5cf6] rounded-full opacity-25 animate-ping"></div>
        <div className="absolute bottom-20 right-1/3 w-24 h-24 bg-gradient-to-r from-[#a855f7] to-[#c084fc] rounded-full opacity-20 animate-pulse"></div>
        
        {/* Additional floating elements */}
        <div className="absolute top-1/2 left-20 w-8 h-8 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-6 h-6 bg-blue-400/20 rounded-full animate-bounce" style={{ animationDelay: '2s' }}></div>
        
        {/* Stripe-inspired floating geometric shapes */}
        <div className="absolute top-10 left-1/2 w-4 h-4 bg-brand-gradient rotate-45 opacity-30 animate-spin" style={{ animationDuration: '8s' }}></div>
        <div className="absolute bottom-40 left-10 w-6 h-6 bg-gradient-to-r from-[#8b5cf6] to-[#a855f7] rotate-12 opacity-25 animate-pulse" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-60 right-10 w-3 h-12 bg-gradient-to-b from-brand-secondary to-transparent opacity-20 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Enhanced grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        {/* Gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/5 via-transparent to-purple-900/5"></div>
      </div>

      {/* Main Content with enhanced styling */}
      <div className="relative z-10 text-center max-w-4xl mx-auto">
        
        {/* Premium Logo Section - Clean Design */}
        <div className="mb-12 relative">
          <div className="absolute inset-0 blur-2xl bg-gradient-to-r from-brand-primary/20 via-brand-secondary/20 to-[#8b5cf6]/20 opacity-60 animate-pulse rounded-full"></div>
          <div className="relative flex justify-center">
            <div className="p-6 bg-white/5 backdrop-blur-xl rounded-2xl shadow-2xl hover:shadow-brand-secondary/20 transition-all duration-300 hover:scale-105">
              <Image
                src="/svg/logo.svg"
                alt="CISCODE Logo"
                width={120}
                height={60}
                className="w-auto h-16 md:h-20 filter brightness-110 hover:brightness-125 transition-all duration-300"
                priority
              />
            </div>
          </div>
          {/* Decorative elements around logo */}
          <div className="absolute -top-2 -left-2 w-4 h-4 bg-gradient-to-r from-brand-secondary to-[#8b5cf6] rounded-full opacity-40 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-gradient-to-r from-[#8b5cf6] to-[#a855f7] rounded-full opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* 404 Number with enhanced animation and glow effect */}
        <div className="mb-8 relative">
          {/* 404 Number with premium glow effects inspired by Linear */}
          <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-brand-primary via-brand-secondary to-[#8b5cf6] opacity-40 animate-pulse"></div>
          <div className="absolute inset-0 blur-2xl bg-gradient-to-r from-brand-secondary to-[#8b5cf6] opacity-20 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <h1 className="relative text-8xl md:text-[12rem] lg:text-[14rem] font-bold bg-gradient-to-r from-brand-primary via-brand-secondary to-[#8b5cf6] bg-clip-text text-transparent animate-pulse drop-shadow-2xl">
            404
          </h1>
          {/* Enhanced sparkle effects */}
          <Sparkles className="absolute top-10 right-10 w-8 h-8 text-brand-secondary animate-pulse" style={{ animationDelay: '0.5s' }} />
          <Sparkles className="absolute bottom-10 left-10 w-6 h-6 text-[#8b5cf6] animate-pulse" style={{ animationDelay: '1.5s' }} />
          <Zap className="absolute top-20 left-20 w-5 h-5 text-[#a855f7] animate-bounce" style={{ animationDelay: '1s' }} />
          <Star className="absolute bottom-20 right-20 w-4 h-4 text-[#c084fc] animate-spin" style={{ animationDuration: '3s' }} />
          
          {/* Vercel-inspired gradient line */}
          <div className="w-40 h-1 bg-gradient-to-r from-brand-primary via-brand-secondary to-[#8b5cf6] mx-auto rounded-full animate-pulse mt-4 shadow-lg"></div>
          <div className="w-20 h-0.5 bg-gradient-to-r from-[#8b5cf6] to-[#c084fc] mx-auto rounded-full animate-pulse mt-2 opacity-70" style={{ animationDelay: '0.8s' }}></div>
        </div>

        {/* Error Message with improved typography */}
        <div className="mb-12 space-y-6">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            {t('title')}
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed opacity-90">
            {t('description')}
          </p>
        </div>

        {/* Premium Action Buttons with Enhanced Styling */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
          <Button
            onClick={handleGoHome}
            className="group relative bg-gradient-to-r from-brand-primary via-brand-secondary to-[#4F7FFF] hover:from-brand-secondary hover:via-[#5B8CFF] hover:to-[#8b5cf6] text-white px-10 py-5 font-bold text-lg transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-brand-secondary/40 overflow-hidden border border-brand-secondary/30 hover:border-[#8b5cf6]/50 backdrop-blur-sm"
          >
            {/* Premium shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform -skew-x-12 group-hover:translate-x-full animate-pulse"></div>
            {/* Inner glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            {/* Animated background overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-secondary/20 via-transparent to-[#8b5cf6]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10 flex items-center justify-center space-x-3 rtl:space-x-reverse">
              <div className="p-1 bg-white/20 group-hover:bg-white/30 transition-colors duration-300">
                <HomeIcon className="w-6 h-6 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
              </div>
              <span className="font-bold tracking-wide">{t('goHome')}</span>
            </div>
            
            {/* Bottom highlight */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-white/50 to-white/30 group-hover:w-3/4 transition-all duration-500"></div>
          </Button>
          
          <Button
            onClick={handleGoBack}
            className="group relative bg-gradient-to-r from-transparent via-[#3F74EF]/10 to-transparent hover:from-brand-secondary/20 hover:via-[#3F74EF]/30 hover:to-[#8b5cf6]/20 text-brand-secondary hover:text-white px-10 py-5 font-bold text-lg transition-all duration-500 hover:scale-110 bg-white/5 backdrop-blur-xl overflow-hidden border-2 border-brand-secondary/40 hover:border-[#8b5cf6]/60 hover:shadow-2xl hover:shadow-brand-secondary/30"
          >
            {/* Premium glass effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-brand-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            {/* Sliding highlight */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3F74EF]/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform translate-x-[-100%] group-hover:translate-x-[100%]"></div>
            {/* Inner border glow */}
            <div className="absolute inset-1 border border-brand-secondary/20 group-hover:border-[#8b5cf6]/40 transition-colors duration-500"></div>
            
            <div className="relative z-10 flex items-center justify-center space-x-3 rtl:space-x-reverse">
              <div className="p-1 bg-brand-secondary/20 group-hover:bg-[#8b5cf6]/30 transition-colors duration-300">
                <ArrowLeftIcon className="w-6 h-6 group-hover:scale-110 group-hover:-translate-x-1 rtl:group-hover:translate-x-1 rtl:rotate-180 transition-transform duration-300" />
              </div>
              <span className="font-bold tracking-wide">{t('goBack')}</span>
            </div>
            
            {/* Corner accents */}
            <div className="absolute top-2 right-2 w-2 h-2 bg-brand-secondary/30 group-hover:bg-[#8b5cf6]/50 transition-colors duration-300"></div>
            <div className="absolute bottom-2 left-2 w-2 h-2 bg-[#8b5cf6]/30 group-hover:bg-brand-secondary/50 transition-colors duration-300"></div>
          </Button>
        </div>

        {/* Enhanced Decorative Elements */}
        <div className="absolute -top-10 -left-10 w-20 h-20 border-2 border-brand-secondary/30 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
        <div className="absolute -bottom-10 -right-10 w-16 h-16 border-2 border-[#8b5cf6]/30 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
        
        {/* Additional decorative rings */}
        <div className="absolute top-20 right-20 w-12 h-12 border border-[#a855f7]/20 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-20 w-8 h-8 border border-[#c084fc]/20 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Enhanced Bottom Wave with gradient */}
      <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent"></div>
    </div>
  );
}
